import React, { useEffect, useRef, useState } from "react";
import { SavedSearchModal } from "./modal/SavedSearchModal";
import { SettingsFooter } from "./modal/footers/SettingsFooter";
import { ModalHeader } from "./modal/ModalHeader";
import { SavedSearchSettingsChannels } from "./channels/SavedSearchSettingsChannels";
import { EditNameButton } from "./edit/EditNameButton";
import { closableErrorToast, shortLivedSuccessToast } from "../utils";
import { DeleteSavedSearchModal } from "./DeleteSavedSearchModal";
import { EditNameSavedSearchModal } from "./EditNameSavedSearchModal";
import {
  BaseSavedSearch,
  Category,
  Channel,
  NotificationChannels,
} from "../model/model";
import { useSavedSearchService } from "../services/useSavedSearchService";
import { useAppTranslation } from "../locales/translate";
import { trackSavedSearchSettings } from "../Tracking";

export interface Props {
  isOpen: boolean;
  closeModal: () => void;
  search: BaseSavedSearch;
  category: Category;
  onSave?: (savedSearch: BaseSavedSearch) => void;
  onDelete?: (id: number) => void;
}

export const EditSavedSearchModal = ({
  isOpen,
  closeModal,
  search,
  category,
  onSave,
  onDelete,
}: Props) => {
  const savedSearchService = useSavedSearchService(category);
  const focusRef = useRef(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [savedSearch, setSavedSearch] = useState<BaseSavedSearch>(search);
  const [enabledChannels, setEnabledChannels] = useState<NotificationChannels>(
    search.notifications
  );

  const { t } = useAppTranslation();

  const closeSettingsModal = () => {
    setIsSettingsOpen(false);
    closeModal();
  };

  useEffect(() => {
    if (isOpen) {
      setIsSettingsOpen(true);
      setSavedSearch(search);
      setEnabledChannels(search.notifications);
      trackSavedSearchSettings.open();
    }
  }, [isOpen]);

  const openDeleteModal = () => {
    setIsSettingsOpen(false);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    closeSettingsModal();
  };

  const onDeleteCancel = () => {
    setIsDeleteOpen(false);
    setIsSettingsOpen(true);
  };

  const openEditModal = () => {
    setIsSettingsOpen(false);
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    setIsEditOpen(false);
    closeSettingsModal();
  };

  const onEditCancel = () => {
    setIsEditOpen(false);
    setIsSettingsOpen(true);
  };

  const isChannelSelected = (channel: Channel) =>
    Boolean(enabledChannels[channel]);

  const updateChannels = (channel: Channel) => {
    const updatedChannels = {
      ...enabledChannels,
      [channel]: !enabledChannels[channel],
    };
    setEnabledChannels(updatedChannels);
    return updatedChannels;
  };

  const toggleChannel = (channel: Channel) => {
    const prevChannels = { ...enabledChannels };
    const updatedChannels = updateChannels(channel);

    savedSearchService
      .updateSavedSearchChannels(savedSearch, updatedChannels)
      .then((updatedSearch) => {
        setSavedSearch({ ...savedSearch, notifications: updatedChannels });
        if (onSave) onSave(updatedSearch);
        shortLivedSuccessToast(t("TOAST_SEARCH_UPDATE_SUCCESS"));
      })
      .catch(() => {
        closableErrorToast(t("TOAST_SEARCH_NOTIFICATION_EDIT_ERROR"));
        setEnabledChannels(prevChannels);
      });
  };

  const onNameSave = (updatedSearch: BaseSavedSearch) => {
    setSavedSearch({ ...savedSearch, name: updatedSearch.name });
    setIsSettingsOpen(true);
    if (onSave) onSave(updatedSearch);
  };

  return (
    <>
      <SavedSearchModal
        isOpen={isSettingsOpen}
        onDismiss={closeSettingsModal}
        focusRef={focusRef}
        body={
          <>
            <ModalHeader title={savedSearch.name} focusRef={focusRef}>
              <EditNameButton onEdit={openEditModal} />
            </ModalHeader>
            <SavedSearchSettingsChannels
              toggleChannel={toggleChannel}
              isChannelSelected={isChannelSelected}
              category={category}
            />
          </>
        }
        footer={<SettingsFooter onDelete={openDeleteModal} />}
      />

      <DeleteSavedSearchModal
        isOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        savedSearchId={savedSearch.id}
        savedSearchName={savedSearch.name}
        category={category}
        onDelete={onDelete}
        onCancel={onDeleteCancel}
      />

      <EditNameSavedSearchModal
        isOpen={isEditOpen}
        closeModal={closeEditModal}
        search={savedSearch}
        category={category}
        onSave={onNameSave}
        onCancel={onEditCancel}
      />
    </>
  );
};
